import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ContextCardProps {
  title: string;
  description: string;
  detail?: string;
}

const DESKTOP_QUERY = '(min-width: 768px)';

export default function ContextCard({ title, description, detail }: ContextCardProps) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_QUERY).matches : false,
  );
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (isDesktop) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-ui-border bg-ui-surface px-5 py-4">
        <Info size={18} className="mt-0.5 shrink-0 text-ui-accent" aria-hidden="true" />
        <div>
          <h2 className="font-medium text-ui-text">{title}</h2>
          <p className="mt-1 text-sm text-ui-text-muted">{description}</p>
          {detail && <p className="mt-2 text-sm text-ui-text-subtle">{detail}</p>}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 self-start rounded-full border border-ui-border bg-ui-surface px-3 py-1.5 text-sm text-ui-text-muted transition-colors hover:border-ui-accent/40 hover:text-ui-text focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent/40"
      >
        <Info size={14} className="shrink-0 text-ui-accent" aria-hidden="true" />
        {title}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-[70] bg-black/50"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />
              <motion.div
                key="sheet"
                role="dialog"
                aria-label={title}
                aria-modal="true"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', stiffness: 380, damping: 38 }}
                className="fixed bottom-0 left-0 right-0 z-[71] rounded-t-2xl border-t border-ui-border bg-ui-surface-2 px-5 pb-8 pt-5 focus:outline-none"
                tabIndex={-1}
              >
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <Info size={18} className="shrink-0 text-ui-accent" aria-hidden="true" />
                    <h2 className="font-medium text-ui-text">{title}</h2>
                  </div>
                  <button
                    type="button"
                    aria-label="Cerrar"
                    onClick={() => { setOpen(false); chipRef.current?.focus(); }}
                    className="-m-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ui-text-muted transition-colors hover:bg-white/5 hover:text-ui-text focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent/40"
                  >
                    <X size={18} aria-hidden="true" />
                  </button>
                </div>
                <p className="text-sm leading-relaxed text-ui-text-muted">{description}</p>
                {detail && <p className="mt-3 text-sm leading-relaxed text-ui-text-subtle">{detail}</p>}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
